import { useState, useEffect, useRef } from 'react'
import { Bell } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../services/api'

function NotificationDropdown({ user }) {
    const [notificationsOpen, setNotificationsOpen] = useState(false)
    const [notifications, setNotifications] = useState([])
    const notificationRef = useRef(null)
    const navigate = useNavigate()

    const unreadCount = notifications.filter(n => !n.is_read).length

    useEffect(() => {
        if (user) {
            loadNotifications()
            const interval = setInterval(loadNotifications, 60000)
            return () => clearInterval(interval)
        }
    }, [user])

    useEffect(() => {
        function handleClickOutside(event) {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setNotificationsOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const loadNotifications = async () => {
        try {
            const data = await api.getNotifications()
            setNotifications(data)
        } catch (error) {
            console.error('Failed to load notifications', error)
        }
    }

    const handleMarkAllRead = async () => {
        const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
        if (unreadIds.length === 0) return;
        await Promise.all(unreadIds.map(id => api.markNotificationAsRead(id)));
        loadNotifications();
    };

    const handleNotificationClick = async (notification) => {
        if (!notification.is_read) {
            try {
                await api.markNotificationAsRead(notification.id)
                setNotifications(prev => prev.map(n =>
                    n.id === notification.id ? { ...n, is_read: 1 } : n
                ))
            } catch (error) {
                console.error('Failed to mark read', error)
            }
        }
        setNotificationsOpen(false)
        if (notification.link) {
            navigate(notification.link)
        }
    }

    return (
        <div
            className="notification-container"
            ref={notificationRef}
            style={{ position: 'relative', flex: '0 0 auto' }}
        >
            <button
                type="button"
                className="btn btn--outline-white btn--small"
                style={{ padding: '0.5rem', position: 'relative' }}
                aria-label="Notifications"
                aria-haspopup="menu"
                aria-expanded={notificationsOpen}
                onClick={() => setNotificationsOpen(!notificationsOpen)}
            >
                <Bell size={20} />
                {unreadCount > 0 && (
                    <span style={{
                        position: 'absolute',
                        top: '-5px',
                        right: '-5px',
                        background: '#ff0000',
                        color: 'white',
                        fontSize: '0.6rem',
                        fontWeight: 'bold',
                        height: '16px',
                        minWidth: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 4px',
                        border: '1px solid black'
                    }}>
                        {unreadCount}
                    </span>
                )}
            </button>

            {notificationsOpen && (
                <div className="card" style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    width: '300px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    marginTop: '0.5rem',
                    color: 'black',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}>
                    <div className="card__header" style={{ padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <h4 className="text-sm font-bold">Notifications</h4>
                            {unreadCount > 0 && <span className="text-xs text-gray">({unreadCount})</span>}
                        </div>
                        {unreadCount > 0 && (
                            <button onClick={handleMarkAllRead} className="btn btn--small btn--outline" style={{ fontSize: '0.7rem', padding: '2px 8px' }}>
                                Mark All Read
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {notifications.length === 0 ? (
                            <div className="p-4 text-center text-sm text-gray">No notifications</div>
                        ) : (
                            notifications.map(notification => (
                                <div
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification)}
                                    className="notification-item"
                                    style={{
                                        padding: '1rem',
                                        borderBottom: '1px solid var(--color-gray-200)',
                                        cursor: 'pointer',
                                        background: notification.is_read ? 'white' : 'var(--color-gray-100)',
                                        transition: 'background 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-gray-200)'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = notification.is_read ? 'white' : 'var(--color-gray-100)'}
                                >
                                    <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>{notification.message}</p>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)' }}>
                                        {new Date(notification.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default NotificationDropdown
